// src/pages/HomePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
    ShoppingBag,
    Play,
    QrCode,
    ArrowLeft,
    ArrowRight,
    Clock,
    Shield,
    Dot,
    Menu
} from "lucide-react";

import { getUser } from "../auth/Auth";
import {
    loadMenusPublic,
    loadTestimonialsPublic,
} from "../common/Common";
import "../assets/css/Homepage.css";

import Facebook from "../assets/images/footer-facebook.png";
import Instagram from "../assets/images/footer-instagram.png";
import Lines from "../assets/images/footer-lines.png";
import AboutLines from "../assets/images/about-lines.png";
import Logo from "../assets/images/logo.png";
import Phone from "../assets/images/call-icon.png";
import Mail from "../assets/images/email-icon.png";
import MapPin from "../assets/images/location-icon.png";

/* ----------------------------- Helpers ----------------------------- */

function FooterSection() {
    const companyLinks = [
        { label: "Our Menu", href: "#collection" },
        { label: "About", href: "#about" },
    ];

    const socials = [
        { icon: Facebook, text: "Follow us on Facebook", href: "#" },
        { icon: Instagram, text: "Follow us on Instagram", href: "#" },
    ];

    const renderSocialButtons = () =>
        socials.map((s) => (
            <a key={s.text} className="footer-keek__socialBtn" href={s.href}>
                <img src={s.icon} alt="" />
                <span>{s.text}</span>
            </a>
        ));

    return (
        <footer className="text-center text-lg-start text-muted footer-keek">
            <img
                src={Lines}
                alt=""
                aria-hidden="true"
                className="footer-keek__lines"
            />
            <section className="container">
                <div className="text-center text-md-start footer-content-wrapper">
                    <div className="footer-keek__layout">
                        {/* Brand */}
                        <div className="footer-keek__brand-col">
                            <div className="footer-keek__brand">
                                <img
                                    src={Logo}
                                    alt="KeeK Colombo"
                                    className="rounded-3"
                                    height={50}
                                    width={50}
                                />
                                <h6 className="text-uppercase fw-bold mb-0 footer-keek__colTitle">
                                    KeeK Colombo
                                </h6>
                            </div>
                        </div>

                        {/* Company links */}
                        <div className="footer-keek__col footer-keek__links">
                            <div className="footer-keek__colTitle mb-3">Company</div>
                            {companyLinks.map((l) => (
                                <a key={l.label} className="footer-keek__link" href={l.href}>
                                    {l.label}
                                </a>
                            ))}
                        </div>

                        {/* Contact + Social */}
                        <div className="footer-keek__col footer-keek__contact">
                            <div className="footer-keek__colTitle mb-3">Contact</div>
                            <a href="#contact" className="footer-keek__link footer-keek__link--plain">
                                Contact Us
                            </a>
                            <a href="tel:+12395550108" className="footer-keek__link">
                                (239) 555-0108
                            </a>
                            <a href="mailto:hello@keekColombo.com" className="footer-keek__link">
                                hello@keekColombo.com
                            </a>
                            <div className="footer-keek__link footer-keek__link--plain">
                                3891 Ranchview Dr.
                                <br />
                                Richardson, California 62639
                            </div>

                            <div className="footer-keek__social footer-keek__social--inline">
                                {renderSocialButtons()}
                            </div>

                            <div className="footer-keek__powered footer-keek__powered--inline">
                                Powered by{" "}
                                <a
                                    href="https://eccelleroit.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="footer-keek__powered-link"
                                >
                                    Eccellero IT
                                </a>
                            </div>
                        </div>

                        <div className="footer-keek__col footer-keek__social-col">
                            <div className="footer-keek__colTitle mb-3">Social</div>
                            <div className="footer-keek__social">
                                {renderSocialButtons()}
                            </div>
                        </div>
                    </div>

                    <div className="footer-keek__bottom">
                        <div className="footer-keek__link small footer-keek__legal">
                            &copy; {new Date().getFullYear()} KeeK Colombo. All rights reserved.
                        </div>
                        <div className="footer-keek__powered footer-keek__powered--bottom">
                            Powered by{" "}
                            <a
                                href="https://eccelleroit.com"
                                target="_blank"
                                rel="noreferrer"
                                className="footer-keek__powered-link"
                            >
                                Eccellero IT
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </footer>
    );
}

export default function HomePage() {
    const [me, setMe] = useState(undefined);
    const [menus, setMenus] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    const primaryNavLinks = [
        { label: "Menu", href: "#collection" },
        { label: "About", href: "#about" },
        { label: "Contact", href: "#contact" },
    ];

    // testimonials scroller
    const trackRef = useRef(null);
    const scroll = (dir) => {
        const el = trackRef.current;
        if (!el) return;
        const amt = Math.min(el.clientWidth * 0.9, 600);
        el.scrollBy({ left: dir * amt, behavior: "smooth" });
    };

    useEffect(() => {
        const el = trackRef.current;
        if (!el) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }

        const update = () => {
            const maxScrollLeft = el.scrollWidth - el.clientWidth;
            setCanScrollLeft(el.scrollLeft > 1);
            setCanScrollRight(maxScrollLeft - el.scrollLeft > 1);
        };

        update();
        el.addEventListener("scroll", update);
        window.addEventListener("resize", update);

        return () => {
            el.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [testimonials]);

    useEffect(() => {
        getUser().then(setMe).catch(() => setMe(null));
    }, []);

    // Load public menus + testimonials (no token)
    useEffect(() => {
        const run = async () => {
            const [ms, ts] = await Promise.all([
                loadMenusPublic().catch(() => []),
                loadTestimonialsPublic().catch(() => []),
            ]);
            setMenus(ms || []);
            setTestimonials(ts || []);
        };
        if (me !== undefined) run();
    }, [me]);

    useEffect(() => {
        const handleResize = () => {
            if (typeof window !== "undefined" && window.innerWidth > 600) {
                setMobileNavOpen(false);
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const loading = me === undefined;
    const featured = useMemo(() => menus.slice(0, 6), [menus]);

    return (
        <div className="bg-white">
            {/* NAV */}
            <header className="hero-nav hero-olive text-white">
                <div className="container py-3">
                    <div className={`header-wrap${mobileNavOpen ? " header-wrap--open" : ""}`}>
                        <div className="header-brand">
                            <div className="header-brand__logo">
                                <img
                                    src={Logo}
                                    alt="KEEK Colombo"
                                    style={{ maxHeight: 40, borderRadius: "20%" }}
                                />
                            </div>
                            <div className="fw-semibold">KEEK Colombo</div>
                        </div>

                        <button
                            type="button"
                            className="hero-nav__toggle"
                            aria-expanded={mobileNavOpen}
                            aria-controls="hero-primary-nav"
                            aria-label="Toggle navigation"
                            onClick={() => setMobileNavOpen((prev) => !prev)}
                        >
                            <span className="visually-hidden">Toggle navigation</span>
                            <Menu size={24} aria-hidden="true" />
                        </button>

                        <nav
                            id="hero-primary-nav"
                            className={`header-links${mobileNavOpen ? " header-links--open" : ""}`}
                        >
                            {primaryNavLinks.map((link) => (
                                <a
                                    key={link.label}
                                    href={link.href}
                                    className="text-white text-decoration-none"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    {link.label}
                                </a>
                            ))}
                            <div className="header-links__order">
                                <a
                                    href="#contact"
                                    className="btn btn-outline-light rounded-pill d-inline-flex align-items-center gap-2"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    Order Now
                                </a>
                            </div>
                        </nav>

                        {/* CTA */}
                        <div className="header-cta">
                            <a
                                className="btn btn-outline-light rounded-pill d-inline-flex align-items-center gap-2"
                                href="#contact"
                                style={{ borderWidth: 2 }}
                            >
                                Order Now
                            </a>
                        </div>
                    </div>
                </div>
            </header>

            {/* HERO */}
            <div className="hero-olive text-white">
                {/* HERO center block + floating photos */}
                <div className="hero-v2 text-white position-relative overflow-hidden">
                    <div className="container py-5 py-lg-6 hero-center-wrap">
                        <div className="hero-content text-center">
                            <div className="small d-inline-flex align-items-center gap-2 opacity-90 mb-3">
                                <span className="hero-dot" />
                                Powering Tomorrow
                            </div>

                            <h1 className="hero-title mb-3">
                                Sweet Stories
                                <br />
                                in Every Bite
                            </h1>

                            <p className="hero-sub">
                                Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sit amet
                                egestas velit. Duis pharetra purus mi, sed feugiat nisi consequat nec.
                                Curabitur eu justo sed nunc malesuada consectetur in eget mauris.
                            </p>

                            <div className="hero-buttons d-flex flex-column flex-md-row align-items-center justify-content-center gap-3 mt-3">
                                <a href="#collection" className="btn btn-saffron rounded-pill px-4 py-2 fw-semibold">
                                    See our cake collection
                                </a>
                                <a href="#contact" className="btn btn-ghost-light rounded-pill px-4 py-2">
                                    Get in touch
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* floating images */}
                    <div className="hero-photo left-main img-card shadow-lift">
                        <img
                            src="https://images.pexels.com/photos/1721932/pexels-photo-1721932.jpeg"
                            alt="Cake left"
                            className="w-100 h-100"
                            style={{ objectFit: "cover" }}
                        />
                    </div>

                    <div className="hero-photo right-top img-card shadow-lift">
                        <img
                            src="https://images.pexels.com/photos/140831/pexels-photo-140831.jpeg"
                            alt="Cake right top"
                            className="w-100 h-100"
                            style={{ objectFit: "cover" }}
                        />
                    </div>

                    {/* two bottom, faded into olive */}
                    <div className="hero-fade-band">
                        <div className="fade-img">
                            <img
                                src="https://images.pexels.com/photos/140831/pexels-photo-140831.jpeg"
                                alt="bottom-1"
                                className="w-100 h-100"
                                style={{ objectFit: "cover" }}
                            />
                        </div>
                    </div>
                    <div className="hero-fade-band-right">
                        <div className="fade-img">
                            <img
                                src="https://images.pexels.com/photos/291528/pexels-photo-291528.jpeg"
                                alt="bottom-2"
                                className="w-100 h-100"
                                style={{ objectFit: "cover" }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* MENU */}
            <section id="collection" className="py-4 py-md-5">
                <div className="container">
                    <p className="mb-0 d-inline-flex align-items-center ">
                        <Dot size={50} className="mx-0 px-0 left_shift_dot" />
                        <span>Our menu</span>
                    </p>
                    <hr className="margin_bottom_height" style={{ borderColor: "var(--olive)", opacity: 0.1 }} />
                    <div className="d-flex align-items-center justify-content-between my-2">
                        <h2 className="h3 about-title mb-0 margin_bottom_height">Our cake collection</h2>
                        {loading && <span className="text-muted small">Loading menus…</span>}
                    </div>

                    {menus.length === 0 && !loading ? (
                        <div className="text-muted">No menu items yet.</div>
                    ) : (
                        <div className="row g-3 g-md-4 mt-1">
                            {featured.map((m, i) => {
                                const img = m.image || m.image_url;
                                const fallback =
                                    "https://images.pexels.com/photos/291528/pexels-photo-291528.jpeg";
                                const priceLabel = `LKR ${m.price ?? ""}`;

                                return (
                                    <div key={m.id || i} className="col-12 col-sm-6 col-lg-4">
                                        <motion.div
                                            whileHover={{ y: -4 }}
                                            className="card card-soft rounded-xxl overflow-hidden h-100"
                                        >
                                            <div className="position-relative">
                                                <img
                                                    src={img || fallback}
                                                    alt={m.name || "Menu item"}
                                                    className="w-100"
                                                    style={{ height: 220, objectFit: "cover" }}
                                                    onError={(e) => {
                                                        e.currentTarget.src = fallback;
                                                    }}
                                                />
                                            </div>

                                            <div className="card-body">
                                                <h5 className="card-title fw-semibold">{m.name}</h5>
                                                <p className="card-text text-muted small mb-3">
                                                    {m.description || "—"}
                                                </p>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="fw-bold" style={{ color: "var(--olive)" }}>
                                                        {priceLabel}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* ABOUT */}
            <section id="about" className="about-wrap position-relative overflow-hidden">
                {/* decorative lines */}
                <img
                    src={AboutLines}
                    alt=""
                    className="about-lines"
                    style={{ opacity: 0.75, height: "100%" }}
                />

                <div className="container position-relative">
                    <p className="mb-0 d-inline-flex align-items-center">
                        <Dot size={50} className="mx-0 px-0 left_shift_dot" />
                        <span>About</span>
                    </p>
                    <hr className="margin_bottom_height" style={{ borderColor: "var(--olive)", opacity: 0.1 }} />
                    <div className="about-card text-center mx-auto">
                        <h2 className="about-title">About us</h2>

                        <p className="about-text">
                            Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sit amet
                            egestas velit. Duis pharetra purus mi, sed feugiat nisi consequat nec.
                            Curabitur eu justo sed nunc malesuada consectetur in eget mauris. Nunc
                            id ultricies nibh. Ut dictum, eros eget auctor ullamcorper, ipsum
                            lectus cursus orci, sit amet hendrerit orci mauris non elit. Duis eget
                            tristique augue. Nulla viverra placerat turpis, lacinia viverra orci
                            dapibus at. Phasellus quis suscipit tellus. Nulla varius libero eu
                            magna posuere ornare.
                        </p>

                        <a href="#more" className="btn btn-outline-dark btn-sm rounded-pill about-cta">
                            Read more
                        </a>
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE US */}
            <section className="py-4 py-md-5" style={{ backgroundColor: "#FAF9F6" }}>
                <div className="container">
                    <div className="why-card rounded-4">
                        <div className="row g-4 g-lg-5 p-0 p-md-0 p-lg-0">
                            {/* Left column: headline + copy */}
                            <div className="col-lg-8">
                                <h3 className="why-title">Why choose us?</h3>
                                <p className="text-muted mb-0" style={{ maxWidth: 400 }}>
                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sit amet
                                    egestas velit. Duis pharetra purus mi, sed feugiat nisi consequat
                                    nec. Curabitur eu justo dui. Ut dictum, eros eget auctor
                                    ullamcorper, ipsum lectus cursus orci, sit amet hendrerit orci
                                    mauris non elit. Duis eget tristique augue. Nulla viverra placerat
                                    turpis, lacinia viverra orci dapibus at. Phasellus quis suscipit
                                    tellus. Nulla varius libero eu magna posuere ornare. Nullam
                                    efficitur eu neque et consequat.
                                </p>
                            </div>

                            {/* Right column: four features in a grid */}
                            <div className="col-lg-4">
                                <div className="row g-5">
                                    <div className="col-sm-12">
                                        <h4 className="why-item-title">Cinematic Stories</h4>
                                        <p className="why-item-text">
                                            Each cake comes with mood videos showcasing Sri Lankan locations
                                        </p>
                                    </div>
                                    <div className="col-sm-12">
                                        <h4 className="why-item-title">QR Experiences</h4>
                                        <p className="why-item-text">
                                            Scan QR codes for immersive audio-visual journeys
                                        </p>
                                    </div>
                                    <div className="col-sm-12">
                                        <h4 className="why-item-title">Premium Quality</h4>
                                        <p className="why-item-text">
                                            Made with finest local ingredients and intentional techniques
                                        </p>
                                    </div>
                                    <div className="col-sm-12">
                                        <h4 className="why-item-title">Island-wide Delivery</h4>
                                        <p className="why-item-text">
                                            Fast delivery across Sri Lanka with cinematic unboxing
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="testimonials-section py-5">
                <div className="container">
                    <div className="testimonials-badge">
                        <Dot size={50} className="mx-0 px-0 left_shift_dot" />
                        <span>Testimonials</span>
                    </div>
                    <hr className="margin_bottom_height" style={{ borderColor: "white", opacity: 1 }} />

                    <div className="testimonials-header">
                        <h2 className="testimonials-title">What our customers say</h2>
                        <div className="testimonials-arrows">
                            <button
                                onClick={() => canScrollLeft && scroll(-1)}
                                className="testimonials-arrow"
                                aria-label="Previous testimonial"
                                title="Previous testimonial"
                                style={{ color: canScrollLeft ? "#ffffff" : "rgba(255,255,255,0.35)" }}
                            >
                                <ArrowLeft size={30} strokeWidth={1.6} />
                            </button>
                            <button
                                onClick={() => canScrollRight && scroll(1)}
                                className="testimonials-arrow"
                                aria-label="Next testimonial"
                                title="Next testimonial"
                                style={{ color: canScrollRight ? "#ffffff" : "rgba(255,255,255,0.35)" }}
                            >
                                <ArrowRight size={30} strokeWidth={1.6} />
                            </button>
                        </div>
                    </div>

                    {loading && <div className="testimonials-loading">Loading testimonials...</div>}

                    {testimonials.length === 0 && !loading ? (
                        <div className="testimonials-empty">No testimonials yet.</div>
                    ) : (
                        <>
                            <div
                                ref={trackRef}
                                className="testimonials-track snap-track scrollbar-custom"
                            >
                                {testimonials.map((t, i) => {
                                    const name = t?.name?.trim() ? t.name : "Customer";
                                    const role = t?.role?.trim();
                                    const message = t?.message?.trim();
                                    // Strictly use backend image_url; fallback avatar only if missing / broken
                                    const src = t.image_url;
                                    const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                        name
                                    )}`;

                                    return (
                                        <div key={t.id || i} className="testimonials-card snap-item">
                                            {message && (
                                                <p className="testimonials-card__message">
                                                    "{message}"
                                                </p>
                                            )}
                                            <div className="testimonials-card__meta">
                                                <img
                                                    src={src || fallback}
                                                    alt={name}
                                                    className="testimonials-card__avatar"
                                                    onError={(e) => {
                                                        e.currentTarget.src = fallback;
                                                    }}
                                                />
                                                <div>
                                                    <div className="testimonials-card__name">{name}</div>
                                                    {role && (
                                                        <div className="testimonials-card__role">{role}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="testimonials-divider testimonials-divider--bottom" />
                        </>
                    )}
                </div>
            </section>

            {/* CONTACT */}
            <section id="contact" className="py-4 py-md-5 ">
                <div className="container">
                    <p className="mb-0 d-inline-flex align-items-center">
                        <Dot size={50} className="mx-0 px-0 left_shift_dot" />
                        <span>Contact us</span>
                    </p>
                    <hr className="margin_bottom_height" style={{ borderColor: "var(--olive)", opacity: 0.1 }} />
                    <h2 className="about-title margin_bottom_height">Get in touch</h2>
                    <p className="text-muted mt-2" style={{ maxWidth: 680 }}>
                        Need a custom cake or have an event coming up? Let's make it cinematic.
                    </p>

                    <div className="contact-grid mt-1">
                        {[
                            {
                                icon: Mail,
                                title: "Email",
                                content: "hello@flow.com",
                                sub: "We will reply within 2 working days",
                            },
                            {
                                icon: Phone,
                                title: "Phone",
                                content: "+94 11 234 5678",
                                sub: "We are available Monday–Friday from 8 AM until 5 PM",
                            },
                            {
                                icon: MapPin,
                                title: "HQ Office",
                                content: "Colombo 07",
                                sub: "Visit us in our office",
                            },
                        ].map((c, i) => (
                            <div key={i} className="bg-white rounded-xxl p-4 h-100 contact-card">
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-3 mb-2"
                                    style={{ width: 56, height: 56, backgroundColor: "rgba(218,165,32,.12)" }}
                                >
                                    <img
                                        src={c.icon}
                                        alt={`${c.title} icon`}
                                        style={{ width: 22, height: 22, objectFit: "contain" }}
                                    />
                                </div>

                                <div className="fw-semibold py-2">{c.title}</div>
                                <div className="text-muted small">{c.sub}</div>
                                <div className="fw-semibold mt-5">{c.content}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <FooterSection />
        </div>
    );
}
